import { GlobalState } from '@/global';
import { BrowserApi } from '@/openapi-clients-generated/auth';
import { FCM } from './fcm';
import { Logger } from './logger';
import type { ServiceWorkerInMessages, ServiceWorkerOutMessages } from './sw.types';

export class ServiceWorkerManager {
    private logger = Logger.createScoped('ServiceWorkerManager');

    private _registration: ServiceWorkerRegistration | null = null;
    private _serviceWorker: ServiceWorker | null = null;

    constructor() {
        this.init();
    }

    get isActive() {
        return this._serviceWorker?.state === 'activated';
    }

    get registration() {
        return this._registration;
    }

    private async init() {
        if (!('serviceWorker' in navigator)) return this.logger.warn('Service Workers are not supported in this browser.');

        navigator.serviceWorker.addEventListener('controllerchange', () => {
            this.logger.info('Service Worker controller changed.');
        });
        navigator.serviceWorker.addEventListener('message', this.handleSwMessage.bind(this));
        navigator.serviceWorker.addEventListener('messageerror', event => this.logger.error('Message error:', event));

        try {
            this.logger.info('Registering Service Worker...');
            // Register as a module so ESM imports in sw.js work
            this._registration = await navigator.serviceWorker.register('/sw.js', { type: 'module' });
        } catch (error) {
            return this.logger.error('Registration failed:', error);
        }

        this._serviceWorker = this._registration.active || this._registration.installing;
        this.logger.info('Registered successfully.');
        this.postMessage('pageLoaded', undefined);

        this._registration.addEventListener('updatefound', () => {
            this.logger.info('Service Worker update found, waiting for activation...');
            this._serviceWorker = this._registration!.installing;
            this._serviceWorker?.addEventListener('error', err => this.logger.error('Service Worker error:', err));
            this._serviceWorker?.addEventListener('statechange', () => {
                this.logger.info(`Service Worker state changed: ${this._serviceWorker!.state}`);
                if (this._serviceWorker!.state === 'activated') this.postPageContext();
            });
        });

        this._registration.update();
    }

    private handleSwMessage(event: MessageEvent) {
        const msg = event.data as unknown;
        if (!msg || typeof msg !== 'object' || !('isZSMessage' in msg)) return;

        this.logger.info('Message received from SW:', msg);

        // oxlint-disable-next-line no-explicit-any
        const isType = <T extends keyof ServiceWorkerOutMessages>(m: any, type: T): m is { type: T; data: ServiceWorkerOutMessages[T] } => {
            return m.type === type;
        };

        if (isType(msg, 'notifyServerNotIdle')) {
            return BrowserApi.postBrowserDeidle();
        }
        if (isType(msg, 'ackNotifications')) {
            return FCM.ackNotifications(msg.data.notificationIds);
        }

        this.logger.warn('Unknown message type from SW:', msg);
    }

    postPageContext() {
        this.postMessage('updatePageContext', {
            modalOpen: document.querySelector('.vf-modal') !== null,
            tenantId: GlobalState.tenant.value?.id ?? null
        });
    }

    postMessage<T extends keyof ServiceWorkerInMessages>(message: T, data: ServiceWorkerInMessages[T]) {
        if (!this._serviceWorker) return this.logger.warn('No Service Worker instance to send message to.');
        this._serviceWorker.postMessage({ isZSMessage: true, type: message, data });
    }
}

export const serviceWorkerManager = new ServiceWorkerManager();
